/**
 * POP Authority Website Publishing Agreement copy (design pa-setup.jsx FullAgreement, PublishingStep
 * and AgreementStep). Shared by the setup wizard's publishing step, where the agreement is signed,
 * and by the packages page, which asks again only when the site has not signed the current version.
 */

export const AGREEMENT_TITLE = 'POP Authority. Website Publishing Agreement'

export const AGREEMENT_PREAMBLE =
  'This Agreement is between PageOptimizer Pro LLC (“POP,” “we,” “us”), 30 N Gould St Ste R, Sheridan, WY 82801, ' +
  'and the Client named above (“you”), for the Website named above (the “Website”), as part of the POP Authority service.'

/** Sections of the full agreement: `blocks` are paragraphs (`p`, optional bold `lead`) or bullet lists (`ul`). */
export const AGREEMENT_SECTIONS = [
  {
    title: '1. What This Covers',
    blocks: [
      {
        p: 'This Agreement covers our access to your Website and our work publishing, updating, and formatting SEO content, metadata, internal links, and schema markup as part of POP Authority. It works alongside your order form or service agreement, which covers pricing, term, and what\'s included in your plan. If the two ever conflict on a publishing-related issue, this Agreement controls.',
      },
    ],
  },
  {
    title: '2. Access We Need',
    blocks: [
      {
        p: 'You\'ll provide our team with our own dedicated website user account, created specifically and exclusively for our use. Please don\'t share a login that you or anyone else also uses. This should be a limited or editor-level account rather than full admin access. You confirm you\'re authorized to grant this access, and that having us publish content you\'ve approved won\'t violate any law, platform rule, or other agreement you\'re bound by.',
      },
    ],
  },
  {
    title: '3. Reviewing and Approving Content',
    blocks: [
      {
        p: 'We\'ll share content with you before it goes live, unless you\'ve given us standing instructions to publish without review. Content counts as approved once any of the following happens:',
        gap: true,
      },
      {
        ul: [
          'You approve it in writing (email is fine);',
          'You\'ve given us standing instructions to publish a category of content (e.g. “post the weekly blog automatically”); or',
          '7 business days pass after we send it to you and you haven\'t objected.',
        ],
      },
      {
        p: 'You\'re responsible for checking content for accuracy, legal and regulatory compliance (this matters especially if you\'re in a regulated industry: health, finance, legal, etc.), correct use of trademarks and testimonials, and any claims made about your business. We\'re not lawyers, doctors, or accountants, and nothing we provide is compliance advice.',
      },
    ],
  },
  {
    title: '4. No Guarantees',
    blocks: [
      {
        p: 'SEO results depend on a lot we don\'t control: search engine algorithms, competitors, your site\'s history, hosting, and your own marketing. We don\'t guarantee rankings, traffic, indexing speed, or revenue. What we do commit to is delivering the work in your plan professionally and on a reasonable timeline.',
      },
    ],
  },
  {
    title: '5. Liability',
    blocks: [
      {
        p: 'Except for gross negligence, willful misconduct, or fraud on our part, our total liability to you under this Agreement is capped at the fees you paid us in the 3 months before the issue arose. Neither of us is liable to the other for indirect, consequential, or lost-profit damages.',
      },
    ],
  },
  {
    title: '6. Who\'s Responsible for What',
    blocks: [
      {
        lead: 'You:',
        p: 'Make sure your Website is backed up before we start work (not necessary if you\'re on a premium hosting or website plan that already handles backups for you), keep your own admin credentials secure, and revoke our access once we\'re done. You\'ll cover any claim made against us arising from content you supplied or approved, or from your Website, business, or marketing claims generally, except where the claim is caused by our own negligence or error.',
        gap: true,
      },
      {
        lead: 'Us:',
        p: 'We\'ll handle your Website carefully, won\'t introduce anything malicious, and won\'t publish anything materially different from what you approved (minor formatting or SEO tweaks aside) without telling you. If we cause any publishing errors without realizing, we\'ll fix it once you let us know.',
      },
    ],
  },
  {
    title: '7. Confidentiality',
    blocks: [
      { p: 'We\'ll each keep your non-public business information confidential and use it only to deliver this engagement.' },
    ],
  },
  {
    title: '8. Ending This Agreement',
    blocks: [
      {
        p: 'Either of us can end this Agreement in writing. If your order form sets a minimum term or notice period, that controls. Once this ends, you\'re responsible for revoking our Website access. Fees for work already completed aren\'t refunded.',
      },
    ],
  },
  {
    title: '9. General',
    blocks: [
      {
        p: 'This Agreement is governed by the laws of the State of Wyoming. If any part of it isn\'t enforceable, the rest still applies. This Agreement plus your order form is the complete agreement between us. Changes need to be in writing and signed by both parties.',
      },
    ],
  },
]

/** Summary bullets above the agreement in the setup wizard's publishing step (design PublishingStep). */
export const AGREEMENT_SETUP_BULLETS = [
  'You give our team a dedicated, editor-level account on your site, created exclusively for our use.',
  'Content counts as approved when you approve it in writing, or when 7 business days pass from the content delivery date without us receiving an objection.',
  'We handle your site carefully and only publish what is approved by the definition above. You are responsible for accuracy, compliance and claims in approved content.',
]

/** Summary bullets on the packages page when the agreement is signed at purchase (design AgreementStep). */
export const AGREEMENT_PURCHASE_BULLETS = [
  'You give our team a dedicated, editor-level account on your site, created exclusively for our use.',
  'We share content with you before it goes live. Content counts as approved when you approve it in writing, when standing instructions cover it, or when 7 business days pass without objection.',
  'We handle your site carefully and publish only what you approved. You are responsible for accuracy, compliance and claims in approved content.',
]

export const AGREEMENT_ACK_TEXT =
  'I have read and agree to the POP Authority Website Publishing Agreement on behalf of my company, ' +
  'and authorize the POP Authority team to publish approved content on my site.'

const MONTHS = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.']

/** Backend timestamps ('YYYY-MM-DD HH:MM:SS', UTC) or ISO strings to a Date; null when unparsable. */
export function parseAgreementDate (value) {
  if (!value) return null
  const text = String(value).trim()
  const iso = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(text) ? text.replace(' ', 'T') + 'Z' : text
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

/** "Sep. 17, 2026" (design date style). */
export function formatAgreementDate (date) {
  const d = date instanceof Date ? date : parseAgreementDate(date)
  if (!d) return ''
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`
}

/** Host of the site URL for the agreement header ("romeonfoot.com"). */
export function agreementWebsiteLabel (url) {
  if (!url) return ''
  try {
    return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.replace(/^www\./, '')
  } catch (e) {
    return String(url)
  }
}
